import React, { useEffect, useLayoutEffect, useState } from 'react';
import { NextPage } from 'next';
import type { AppContext, AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { Inter, Outfit } from 'next/font/google';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { LightThemeProvider } from '@lemon-io/ui-kit';
import CssBaseline from '@mui/material/CssBaseline';
import { AppCacheProvider } from '@mui/material-nextjs/v14-pagesRouter';
import { LicenseInfo } from '@mui/x-license-pro';
import {
  HydrationBoundary,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { BotIdClient } from 'botid/client';
import { getCookie } from 'cookies-next';
import { TmpCookiesObj } from 'cookies-next/lib/types';
import mixpanel from 'mixpanel-browser';

import { SIGNUP_REGISTER_API_PATH } from '@internal/api/signup/constants';
import { extractToken } from '@internal/api/system/utils';
import CountryResolver from '@internal/components/common/country-resolver/country-resolver';
import { useUtmParamsCapture } from '@internal/components/escape-the-matrix/utm-context';
import { SnackbarsProvider } from '@internal/components/snackbars';
import { muiLicense } from '@internal/config/app';
import useGtm from '@internal/hooks/use-gtm';
import MixpanelService from '@internal/services/mixpanel-service';
import { MEvents, PageViewedMEvent } from '@internal/utils/mixpanel';

import 'styles/font.css';

const Layout = dynamic(() => import('@internal/components/common/layout'));

LicenseInfo.setLicenseKey(muiLicense);

export type NextPageWithLayout<P = unknown, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: React.ReactElement) => React.ReactNode;
};

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
  pageProps: AppProps['pageProps'] & {
    withoutLayout?: boolean;
    dehydratedState?: Record<string, unknown>;
    onlyHeader?: boolean;
    withoutPadding?: boolean;
    lightBackground?: boolean;
    sideNav?: boolean;
    appTheme?: boolean;
    token: string;
  };
};

export const outfit = Outfit({
  display: 'swap',
  weight: ['400', '500', '600', '700'],
  subsets: ['latin'],
});

export const inter = Inter({
  display: 'swap',
  subsets: ['latin'],
});

interface ProtectedRoute {
  method: string;
  path: string;
}

const PROTECTED_ROUTES: ProtectedRoute[] =
  process.env.NEXT_PUBLIC_ENVIRONMENT === 'production'
    ? [
        {
          path: '/api/escape-the-matrix/register',
          method: 'POST',
        },
        {
          path: SIGNUP_REGISTER_API_PATH,
          method: 'POST',
        },
      ]
    : [];
export interface MyAppProps extends AppPropsWithLayout {
  token: string;
}
function MyApp(props: MyAppProps) {
  const [eventSent, setEventSent] = useState(false);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: false,
            retry: 2,
          },
        },
      })
  );

  const { Component, pageProps } = props;

  useGtm('GTM-TJTHKK6');
  useUtmParamsCapture();

  const router = useRouter();

  useLayoutEffect(() => {
    MixpanelService.init();
    MixpanelService.addEvent(MEvents.SessionStart);

    return () => {
      MixpanelService.addEvent(MEvents.SessionEnd);
    };
  }, []);

  useEffect(() => {
    const handleRouteChange = (url: string) => {
      (window as { hj?: (event: string, data?: string) => void }).hj?.(
        'stateChange',
        url
      );

      if (!url.includes('escape-the-matrix')) {
        PageViewedMEvent(url);
        return;
      }

      if (eventSent || getCookie('authDomainSSO')) {
        return;
      }

      setEventSent(true);
      mixpanel.track('me-lemon_registration_form_viewed');
    };
    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, eventSent]);

  const getLayout: (page: React.ReactElement) => React.ReactNode =
    pageProps?.withoutLayout
      ? page => page
      : page => (
          <Layout
            onlyHeader={!!pageProps?.onlyHeader}
            withoutPadding={!!pageProps?.withoutPadding}
            lightBackground={!!pageProps?.lightBackground}
            sideNav={!!pageProps?.sideNav}
          >
            {page}
          </Layout>
        );

  return (
    <AppCacheProvider {...props}>
      <Head>
        <meta name="viewport" content="initial-scale=1, width=device-width" />
        <BotIdClient protect={PROTECTED_ROUTES} />
      </Head>
      <Script
        id="hotjar-script"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `(function(h,o,t,j,a,r){
          h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
          h._hjSettings={hjid:1723409,hjsv:6};
          a=o.getElementsByTagName('head')[0];
          r=o.createElement('script');r.async=1;
          r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
          a.appendChild(r);
      })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`,
        }}
      />
      {/* eslint-disable-next-line react/no-unknown-property */}
      <style jsx global>{`
        html,
        body {
          font-family: ${pageProps?.sideNav || pageProps?.appTheme
            ? inter.style.fontFamily
            : outfit.style.fontFamily};
        }

        #__next {
          height: 100vh;
        }
      `}</style>
      <QueryClientProvider client={queryClient}>
        <LightThemeProvider>
          <CssBaseline />
          <HydrationBoundary state={pageProps?.dehydratedState}>
            <SnackbarsProvider>
              {getLayout(<Component {...pageProps} />)}
              <CountryResolver />
            </SnackbarsProvider>
          </HydrationBoundary>
        </LightThemeProvider>
        <ReactQueryDevtools initialIsOpen={false} position="right" />
      </QueryClientProvider>
    </AppCacheProvider>
  );
}

MyApp.getInitialProps = ({ ctx }: AppContext) => {
  const token = extractToken(ctx);

  const { req, res } = ctx;

  const isServer = res && typeof res?.writeHead === 'function';

  if (isServer && ctx.asPath === '/' && token) {
    res.writeHead(307, {
      Location: `${process.env.NEXT_PUBLIC_APP_URL}/roles`,
    });
    res.end();

    return {};
  }

  return {
    token,
    cookies: (req as { cookies?: TmpCookiesObj } | undefined)?.cookies,
  };
};

export default MyApp;
